/**
 * Modus B — de melding als dialoog.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/spec.md FR-14, FR-16, FR-17
 * Plan: §6.2, §6.5, §6.6
 * Taken: T-2.05, T-2.06, T-2.07, T-2.14, T-2.15
 *
 * ── DIT IS GEEN VERBOUWDE `CommandOverlay` ──────────────────────────────────
 *
 * `CommandOverlay` blijft het vangnet voor `show_message` op 800+ tablets met gemengde
 * bundles. Er een tweede betekenis in duwen zou betekenen dat elke oudere app-versie
 * meeverandert — en juist die versies zijn degene die we niet kunnen testen. Dit is een
 * apart component op een eigen laag (`Z_INDEX.NOTIFICATION_DIALOG`, 9950), boven het
 * vangnet (9940) en onder de noodmelding (9975) en een oproep (9990).
 *
 * ── DE AFTELRING IS ZICHTBAAR, EN DAT IS EEN ONTWERPEIS ─────────────────────
 *
 * Een melding die na dertig seconden zomaar verdwijnt, is voor dit publiek verwarrend: was
 * er iets, heb ik iets gemist, deed ik iets fout? De ring loopt leeg terwijl je kijkt, dus
 * het verdwijnen is aangekondigd in plaats van plotseling. Bij `requiresAck` is er GEEN
 * aftelling — dan blijft hij staan tot de bewoner hem wegklikt.
 *
 * ── DE ACK KOMT NA HET SCHILDEREN, NIET BIJ ONTVANGST ───────────────────────
 *
 * `presentAndSound()` loopt in een `requestAnimationFrame`, dus nadat de browser dit
 * component daadwerkelijk getekend heeft. `pushed` betekent dat WIJ het kanaal aanspraken;
 * `displayed` betekent dat de TABLET het toonde. Acken bij ontvangst van het commando zou
 * precies de leugen zijn die deze hele feature moet uitsluiten.
 *
 * ── EERST TONEN, DAN GELUID ─────────────────────────────────────────────────
 *
 * Er wordt niets geawait vóór het renderen. Het geluid wordt ná de eerste frame opgehaald
 * (cache → netwerk met 3s → ingebouwd → stil) en de uitkomst gaat als `soundPlayed` mee in
 * dezelfde ack. Is autoplay geblokkeerd, dan staat de melding er nog steeds volledig — en
 * trilt het toestel. Geluid is nooit de drager van de boodschap.
 *
 * ── KLEUREN KOMEN UIT DE THEMATOKENS ────────────────────────────────────────
 *
 * ⚠ Geen hardgecodeerde hexes. Het thema is per locatie instelbaar (licht / donker /
 * hoog-contrast), dus een vaste kleur is altijd ergens fout. Dat is in
 * `AnnouncementBanner.tsx` expliciet gerepareerd nadat een pastelpalet in donkere modus
 * onleesbaar omklapte, en het mag hier niet terugkomen.
 *
 * ── CHROMIUM 84 ─────────────────────────────────────────────────────────────
 *
 * Geen `:has()`, geen `structuredClone`, geen `AbortSignal.timeout`. De aftelring is een
 * gewone SVG met `stroke-dasharray`; die werkt al sinds ruim vóór die ondergrens.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Z_INDEX } from '../constants/zIndex';
import * as NotificationStore from '../services/NotificationStore';
import { useClaimedNotification } from '../hooks/useResidentNotifications';
import { useCurrentCall } from '../hooks/useSip';
import { NotificationActions } from './NotificationActions';
import type { ResidentNotificationPriority } from '@caro/shared/types';

/** Prioriteit → thematoken. Zie de kop: nooit een hex. */
const PRIORITY_ACCENT: Record<ResidentNotificationPriority, string> = {
  urgent: 'var(--caro-error)',
  high: 'var(--caro-warning)',
  normal: 'var(--caro-primary)',
  low: 'var(--caro-success)',
};

/**
 * Kleur is nooit de enige drager. Elk niveau krijgt ook een icoon én een woord, zodat de
 * betekenis overkomt op een vergeeld scherm, in hoog-contrast en bij kleurenblindheid.
 */
const PRIORITY_ICON: Record<ResidentNotificationPriority, string> = {
  urgent: '⚠',
  high: '❗',
  normal: 'ℹ',
  low: '·',
};

const PRIORITY_WORD: Record<ResidentNotificationPriority, string> = {
  urgent: 'Dringend',
  high: 'Belangrijk',
  normal: 'Bericht',
  low: 'Ter info',
};

const RING_SIZE = 56;
const RING_STROKE = 6;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Eén tik per 200ms: vloeiend genoeg om te zien, rustig genoeg voor een trage WebView. */
const TICK_MS = 200;

export function NotificationDialog() {
  const claimed = useClaimedNotification();
  const payload = claimed ? claimed.notification : null;

  // Alleen de dialoogmodus. `fullpage` komt in fase 3 en heeft zijn eigen component; hem
  // hier stilzwijgend als dialoog tonen zou betekenen dat een auteur iets anders krijgt dan
  // hij configureerde, zonder dat iemand dat merkt.
  const isDialog = payload != null && payload.presentation === 'dialog';

  if (!isDialog || !payload || !claimed) return null;

  return (
    <DialogCard
      key={payload.notificationId}
      notificationId={payload.notificationId}
      title={payload.title}
      body={payload.body}
      priority={payload.priority}
      displaySeconds={payload.requiresAck ? null : payload.displaySeconds}
      requiresAck={payload.requiresAck}
      payload={payload}
    />
  );
}

interface DialogCardProps {
  notificationId: string;
  title: string;
  body?: string;
  priority: ResidentNotificationPriority;
  displaySeconds: number | null;
  requiresAck: boolean;
  payload: NonNullable<ReturnType<typeof NotificationStore.getClaimedSnapshot>>['notification'];
}

function DialogCard({
  notificationId,
  title,
  body,
  priority,
  displaySeconds,
  requiresAck,
  payload,
}: DialogCardProps) {
  const accent = PRIORITY_ACCENT[priority] || PRIORITY_ACCENT.normal;
  const totalMs = displaySeconds != null ? Math.max(1, displaySeconds) * 1000 : null;

  // ── T-5.03 / T-3.04: een gesprek legt de melding stil ────────────────────
  //
  // `useCurrentCall()` leest dezelfde modulestatus als `useSip()` maar draagt geen van de
  // vier effecten die daar SIP verbinden en bij uitloggen weer afbreken — die horen niet in
  // een meldingsdialoog.
  //
  // Wat er stopt is het GELUID en de STEM: op een handsfree wandtablet komt alles uit
  // dezelfde luidspreker, en door een gesprek heen praten is niet hinderlijk maar
  // onverstaanbaar. De aftelling pauzeert mee — anders loopt een melding van dertig
  // seconden ongezien af tijdens een gesprek van twee minuten, en is `displayed` waar
  // terwijl niemand hem gelezen heeft.
  const call = useCurrentCall();
  const paused = call !== null;

  const startedAt = useRef<number>(Date.now());
  const [remainingMs, setRemainingMs] = useState<number | null>(totalMs);

  // ── T-2.06 / T-2.14: acken NA het schilderen, geluid daarna ──────────────
  //
  // Twee geneste frames: de eerste wordt aangevraagd tijdens de commit, de tweede loopt
  // pas nadat de browser echt getekend heeft. Eén frame is op een trage WebView niet altijd
  // genoeg, en dan zou `displayed` weer een belofte zijn in plaats van een waarneming.
  useEffect(() => {
    let cancelled = false;
    const outer = requestAnimationFrame(() => {
      const inner = requestAnimationFrame(() => {
        if (cancelled) return;
        NotificationStore.presentAndSound(payload);
      });
      // Opruimen van de binnenste frame loopt via de vlag; hem hier bewaren zou een tweede
      // ref kosten voor iets wat één tik duurt.
      void inner;
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outer);
    };
    // Bewust op het ID en niet op het hele object: een `replace` van dezelfde melding mag
    // geen tweede ack en geen tweede geluid opleveren.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationId]);

  useEffect(() => {
    if (!paused) return;
    NotificationStore.stopSound();
    void NotificationStore.stopTts();
  }, [paused]);

  // ── De aftelling ─────────────────────────────────────────────────────────
  //
  // `startedAt` wordt bij het hervatten opnieuw gezet, zodat de verstreken gesprekstijd
  // niet alsnog van de schermtijd afgaat.
  useEffect(() => {
    if (totalMs == null) return undefined;
    if (paused) return undefined;
    startedAt.current = Date.now() - (totalMs - (remainingMs ?? totalMs));
    const timer = setInterval(() => {
      const left = totalMs - (Date.now() - startedAt.current);
      if (left <= 0) {
        clearInterval(timer);
        setRemainingMs(0);
        // Automatisch verdwijnen is GEEN `acknowledged`: de bewoner heeft niets bevestigd.
        // Die twee door elkaar halen zou de statistiek "hoeveel meldingen worden echt
        // gelezen" waardeloos maken.
        NotificationStore.dismissClaimed(notificationId, false);
      } else {
        setRemainingMs(left);
      }
    }, TICK_MS);
    return () => clearInterval(timer);
    // `remainingMs` staat bewust NIET in de deps: hij verandert elke tik en zou de timer
    // dan telkens opnieuw opzetten. Hij wordt alleen bij het (her)starten uitgelezen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalMs, notificationId, paused]);

  // Bij het verlaten van het scherm stopt het geluid. Anders speelt een chime van tien
  // seconden door terwijl de melding allang weg is.
  useEffect(() => () => {
    NotificationStore.stopSound();
    void NotificationStore.stopTts();
  }, []);

  const fraction = useMemo(() => {
    if (totalMs == null || remainingMs == null) return 1;
    return Math.max(0, Math.min(1, remainingMs / totalMs));
  }, [totalMs, remainingMs]);

  const secondsLeft = remainingMs == null ? null : Math.ceil(remainingMs / 1000);

  const close = () => NotificationStore.dismissClaimed(notificationId, true);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="caro-notif-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.NOTIFICATION_DIALOG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // De dimlaag onderbreekt tegelijk de screensaver visueel: elke meldingslaag zit
        // boven de stand-byband, dus dit valt er hoe dan ook overheen.
        backgroundColor: 'rgba(0,0,0,0.55)',
        padding: 16,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 640,
          backgroundColor: 'var(--caro-card-bg)',
          color: 'var(--caro-text)',
          borderRadius: 20,
          // De prioriteit zit in een rand, niet in een vlakvulling: gekleurde tekst op een
          // gekleurd vlak haalt in hoog-contrast het contrastminimum niet.
          borderTop: `8px solid ${accent}`,
          boxShadow: 'var(--caro-card-shadow, 0 12px 48px rgba(0,0,0,0.45))',
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <span aria-hidden="true" style={{ fontSize: 34, color: accent, lineHeight: 1 }}>
            {PRIORITY_ICON[priority]}
          </span>
          <span
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: accent,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            {PRIORITY_WORD[priority]}
          </span>
          <span style={{ flex: 1 }} />
          {totalMs != null && <CountdownRing fraction={fraction} seconds={secondsLeft} accent={accent} />}
        </div>

        <h2 id="caro-notif-title" style={{ margin: 0, fontSize: 28, lineHeight: 1.25, fontWeight: 700 }}>
          {title}
        </h2>

        {body && (
          <p style={{ margin: 0, fontSize: 21, lineHeight: 1.5, opacity: 0.92 }}>{body}</p>
        )}

        {/* Fase 4. De knoppen staan BOVEN "Sluiten": de bedoelde handeling hoort onder
            de tekst te staan, en de uitgang eronder — niet andersom. Een mislukte
            serveractie zet een zichtbare foutstaat op de knop en laat de melding staan
            (T-4.08); geen groen vinkje op een commando dat niet aankwam. */}
        <NotificationActions
          notificationId={notificationId}
          actions={payload.actions}
          onClose={(ack) => NotificationStore.dismissClaimed(notificationId, ack)}
          minHeight={64}
        />

        <button
          type="button"
          onClick={close}
          style={{
            // ≥ 64px: primaire actie volgens de ouderentoegankelijkheidsnorm uit het plan.
            minHeight: 64,
            padding: '16px 24px',
            borderRadius: 14,
            border: 'none',
            backgroundColor: accent,
            color: 'var(--caro-card-bg)',
            fontSize: 22,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Sluiten
        </button>

        {requiresAck && (
          <p style={{ margin: 0, fontSize: 16, opacity: 0.7, textAlign: 'center' }}>
            Deze melding blijft staan tot u hem sluit.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * De aftelring.
 *
 * Twee cirkels: een spoor en een boog die leegloopt. Het getal staat er ook, want een ring
 * alleen is voor een deel van dit publiek te subtiel — en kleur en vorm mogen nooit de
 * enige dragers zijn.
 */
function CountdownRing({
  fraction,
  seconds,
  accent,
}: {
  fraction: number;
  seconds: number | null;
  accent: string;
}) {
  const offset = RING_CIRCUMFERENCE * (1 - fraction);
  return (
    <span
      role="timer"
      aria-live="off"
      aria-label={seconds != null ? `Sluit over ${seconds} seconden` : undefined}
      style={{ position: 'relative', width: RING_SIZE, height: RING_SIZE, flexShrink: 0 }}
    >
      <svg width={RING_SIZE} height={RING_SIZE} aria-hidden="true">
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={RING_STROKE}
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke={accent}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
          // Vanaf twaalf uur, met de klok mee leeglopend.
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          // Geen animatie boven 200ms — dat is ook precies de tikfrequentie, dus de ring
          // beweegt vloeiend zonder dat er iets te "vangen" valt.
          style={{ transition: `stroke-dashoffset ${TICK_MS}ms linear` }}
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {seconds != null ? seconds : ''}
      </span>
    </span>
  );
}
